import { NO_VALUE } from '../../lib/utils'
import { formatDuration } from '../../lib/format'
import { useUnits } from '../../lib/UnitsProvider'
import { TOKENS } from '../ui/dashboard-primitives'

export interface SetRow {
  label: string
  reps: number | null
  weight: number | null
  durationSec: number | null
  metric: 'reps' | 'seconds'
  repsRight: number | null
  durationSecRight: number | null
  skipped?: boolean
  /** Card-level: this exercise is logged per side (D12). */
  perSide?: boolean
}

/** `12`, or `12 / 10` when the two sides genuinely differ (C-D3). */
function sided(base: number | null, right: number | null, fmt: (n: number) => string): string {
  if (base == null) return NO_VALUE
  if (right == null || right === base) return fmt(base)
  return `${fmt(base)} / ${fmt(right)}`
}

export function SetGrid({ rows, idxLabel }: { rows: SetRow[]; idxLabel: 'Set' | 'Round' }) {
  const { formatLoadParts } = useUnits()
  // Columns follow the exercise's metric (C-D1). Each card renders its own
  // grid, so the header costs nothing and no row ever carries a dash for a
  // dimension the exercise does not have.
  const timed = rows.length > 0 && rows[0].metric === 'seconds'
  // A bodyweight movement shows no weight column at all. `weight_kg` on such
  // an exercise means ADDED load (D4), so a stored 0 means "nothing added",
  // and rendering it as "0 kg" reads as "no resistance".
  const showWeight = rows.some((r) => r.weight != null && r.weight > 0)
  const cols = ['3rem', '1fr', showWeight ? '1fr' : ''].filter(Boolean)
  const gridTemplateColumns = cols.join(' ')

  return (
    <div className="flex flex-col gap-[.35rem]">
      <div className="grid items-center px-[.65rem]" style={{ gridTemplateColumns, gap: '.5rem' }}>
        <span style={cellK}>{idxLabel}</span>
        <span style={cellK}>{timed ? 'Time' : 'Reps'}</span>
        {showWeight ? <span style={cellK}>Weight</span> : null}
      </div>
      {rows.map((r, i) => (
        <div key={i} className="grid items-center px-[.65rem] py-[.42rem] rounded-lg"
          // eslint-disable-next-line no-restricted-syntax -- pre-existing color-literal drift, tracked in docs/refactor-suggestions.md Phase 0/2
          style={{ gridTemplateColumns, gap: '.5rem', background: 'rgba(245,237,225,.03)', opacity: r.skipped ? 0.4 : 1 }}>
          <span style={idxStyle}>{r.label}</span>
          <span style={valStyle}>
            {r.skipped
              ? 'skipped'
              : timed
                ? sided(r.durationSec, r.durationSecRight, formatDuration)
                : sided(r.reps, r.repsRight, String)}
          </span>
          {showWeight ? (
            <span style={valStyle}>
              {r.skipped || r.weight == null || r.weight === 0
                ? NO_VALUE
                : (() => {
                    const { value, unit } = formatLoadParts(r.weight)
                    return (
                      <>
                        {value} <span style={unitStyle}>{unit}</span>
                      </>
                    )
                  })()}
            </span>
          ) : null}
        </div>
      ))}
    </div>
  )
}
export function MetricGrid({ items }: { items: [string, string, string][] }) {
  return (
    <div className="grid gap-[.5rem]" style={{ gridTemplateColumns: 'repeat(4,1fr)' }}>
      {items.map(([k, v, u], i) => (
        // eslint-disable-next-line no-restricted-syntax -- pre-existing color-literal drift, tracked in docs/refactor-suggestions.md Phase 0/2
        <div key={i} className="rounded-lg" style={{ padding: '.55rem .65rem', background: 'rgba(245,237,225,.03)' }}>
          <div style={cellK}>{k}</div>
          <div style={{ ...valStyle, marginTop: '.28rem', fontSize: '1.05rem' }}>{v}<span style={unitStyle}>{u}</span></div>
        </div>
      ))}
    </div>
  )
}
const mono = "'Geist Mono', ui-monospace, monospace"
const cellK: React.CSSProperties = { fontFamily: mono, fontSize: 8.5, letterSpacing: '.16em', textTransform: 'uppercase', color: TOKENS.creamFaint }
const idxStyle: React.CSSProperties = { fontFamily: mono, fontSize: 9, letterSpacing: '.14em', textTransform: 'uppercase', color: TOKENS.creamFaint }
const valStyle: React.CSSProperties = { fontFamily: mono, fontSize: '.95rem', fontWeight: 500, color: TOKENS.cream, fontVariantNumeric: 'tabular-nums', lineHeight: 1.1 }
const unitStyle: React.CSSProperties = { fontSize: '.62em', color: TOKENS.creamDim, marginLeft: '.15em' }
